import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useState
} from 'react';
import { Platform } from 'react-native';
import { useUserContext } from './UserContext';
import { GoogleFitHealth } from '../tools/google-fit';
import { HealthKitSync } from '../tools/health-kit';
import { addDays, format } from 'date-fns';
import { UserAttribute, UserAttributeSource } from '../models/UserAttribute';
import { useDeviceSyncContext } from './DeviceSyncContext';
import { useFindLatestAttributes } from '../hooks/user/useFindLatestAttributes';
import { FetchError } from '../api/api';
import _ from 'lodash';
import { GoogleHealthConnect } from '../tools/google-health-connect';
import {
  checkPhysicalActivityPermission,
  showPhysicalActivityAlert
} from '../tools/google-fit.native';
import {
  displayedWearableConnection,
  phoneHealthIsConnected
} from '../tools/WearableConnection';

export interface UserAttributesInfo {
  deviceName?: string;
  deviceKey: UserAttributeSource | string;
  startedAt?: Date;
  succeededAt?: Date;
  message?: string;
  sent?: number;
  total?: number;
}

export type UserAttributesContextState = {
  latestAttributes?: UserAttribute[];
  loadingLatestAttributes: boolean;
  userAttributesError?: FetchError;
  synchronizingData?: boolean;
  connectedWearableSources?: UserAttributeSource[];
  syncData: (
    userId: number,
    connectedWearableSources: UserAttributeSource[],
    forceBackgroundSync?: boolean,
    silent?: boolean
  ) => Promise<void>;
  checkConnectedWearables: () => void;
};

export const UserAttributesContext = createContext<UserAttributesContextState>(
  null!
);

export const useUserAttributesContext = () => {
  const context = useContext(UserAttributesContext);
  if (!context) {
    throw new Error(
      'useUserAttributesContext must be used within the context of the UserAttributesProvider component'
    );
  }
  return context;
};

export const UserAttributesProvider: React.FC<
  PropsWithChildren & { embedded?: boolean }
> = ({ embedded, children }) => {
  const { user } = useUserContext();
  const [latestAttributes, setLatestAttributes] = useState<
    UserAttribute[] | undefined
  >();
  const { synchronizeData } = useDeviceSyncContext();
  const [synchronizingData, setSynchronizingData] = useState<boolean>();
  const { loadingLatestAttributes, userAttributesError, findLatestAttributes } =
    useFindLatestAttributes();
  const [connectedWearableSources, setConnectedWearableSources] = useState<
    UserAttributeSource[]
  >(embedded ? Object.values(UserAttributeSource) : []);

  const checkConnectedWearables = useCallback(async () => {
    const sources: UserAttributeSource[] = [];
    if (embedded) return;
    if (user?.id) {
      const connection = displayedWearableConnection(user);
      if (Platform.OS === 'ios') {
        const healthKit = new HealthKitSync(user.id);
        const connected = await healthKit.getAuthStatus();
        if (
          await phoneHealthIsConnected(
            UserAttributeSource.HealthKit,
            connected,
            user
          )
        ) {
          sources.push(UserAttributeSource.HealthKit);
        }
      } else if (Platform.OS === 'android') {
        const googleFit = new GoogleFitHealth(user.id);
        const connected = await googleFit.getIsGoogleFitAuthorized();
        if (connected) {
          const permission = await checkPhysicalActivityPermission();
          if (permission === 'never_ask_again') {
            await showPhysicalActivityAlert();
          } else if (
            await phoneHealthIsConnected(
              UserAttributeSource.GoogleFit,
              true,
              user
            )
          ) {
            sources.push(UserAttributeSource.GoogleFit);
          }
        }
        const healthConnect = new GoogleHealthConnect(user.id);
        const initialized = await healthConnect.authorize();
        if (initialized) {
          const permissions = await healthConnect.getGrantedPermissions();
          if (
            await phoneHealthIsConnected(
              UserAttributeSource.HealthConnect,
              permissions.length > 0,
              user
            )
          ) {
            sources.push(UserAttributeSource.HealthConnect);
          }
        }
      }

      if (connection?.fitbitAccessToken) {
        sources.push(UserAttributeSource.Fitbit);
      }
      if (connection?.garminConfig?.accessToken) {
        sources.push(UserAttributeSource.Garmin);
      }
      if (connection?.ouraAccessToken) {
        sources.push(UserAttributeSource.Oura);
      }
    }
    // This is to prevent unnecessary rerender when sources are the same
    if (_.isEqual(sources, connectedWearableSources)) return;
    setConnectedWearableSources(sources);
  }, [
    embedded,
    connectedWearableSources,
    user?.fitbitAccessToken,
    user?.garminConfig?.accessToken,
    user?.id,
    user?.ouraAccessToken,
    user?.wearableConnection
  ]);

  const syncData = useCallback(
    async (
      userId: number,
      connectedWearableSources: UserAttributeSource[],
      forceBackgroundSync?: boolean,
      silent?: boolean
    ) => {
      if (!silent) setSynchronizingData(true);
      if (forceBackgroundSync && !embedded) {
        for await (const source of connectedWearableSources) {
          await synchronizeData(source);
        }
      }
      const startDate = format(addDays(new Date(), -30), 'yyyy-MM-dd');
      const endDate = format(new Date(), 'yyyy-MM-dd');
      const result =
        (await findLatestAttributes(
          userId,
          connectedWearableSources,
          startDate,
          endDate
        )) || [];

      if (embedded) {
        const sources: UserAttributeSource[] = [];
        Object.values(UserAttributeSource).forEach(source => {
          if (result.find(r => r.source === source)) sources.push(source);
        });
        if (!_.isEqual(sources, connectedWearableSources))
          setConnectedWearableSources(sources);
      }

      setLatestAttributes(
        result.sort(
          (objA, objB) =>
            new Date(objB.endDate).getTime() - new Date(objA.endDate).getTime()
        )
      );
      setSynchronizingData(false);
    },
    [embedded, findLatestAttributes, synchronizeData]
  );

  return (
    <UserAttributesContext.Provider
      value={{
        latestAttributes,
        loadingLatestAttributes,
        userAttributesError,
        synchronizingData,
        connectedWearableSources,
        syncData,
        checkConnectedWearables
      }}
    >
      {children}
    </UserAttributesContext.Provider>
  );
};
